import React,{useState,useEffect,useContext} from 'react';
import {useOutletContext} from 'react-router-dom';
import SignatureCanvas from './SignatureCanvas';
import FileImport from './FileImport';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

function ConfigPage({config,updateConfig,clearAllData,usersWithGroups,onUsersUpdate}) {
  const [newGarment,setNewGarment]=useState('');
  const [newMeasurerName,setNewMeasurerName]=useState('');
  const [newGarmentSizeType,setNewGarmentSizeType]=useState('american');
  const [newGarmentSizeRange,setNewGarmentSizeRange]=useState({min: 'XS',max: '6XL'});
  const [activeStep,setActiveStep]=useState(0);
  const [completedSteps,setCompletedSteps]=useState([]);
  const [isSaving,setIsSaving]=useState(false);
  const {onConfigSaved}=useOutletContext();

  // Determinar si es el primer acceso basado en la configuración
  const isFirstAccess=!config.clientName || !config.garmentTypes || config.garmentTypes.length===0;

  // Pasos del onboarding
  const onboardingSteps=[
    {id: 0,title: 'Datos del Cliente',description: 'Configura el nombre y logo del cliente'},
    {id: 1,title: 'Personal de Tallaje',description: 'Añade al menos un miembro del personal'},
    {id: 2,title: 'Tipos de Prendas',description: 'Define las prendas que se medirán'}
  ];

  // Efecto para verificar pasos completados
  useEffect(()=> {
    const steps=[];
    // Paso 1: Datos del cliente completado
    if (config.clientName && config.clientName.trim() !=='') {
      steps.push(0);
    }
    // Paso 2: Al menos un tallador
    if (config.measurers && config.measurers.length > 0) {
      steps.push(1);
    }
    // Paso 3: Al menos una prenda
    if (config.garmentTypes && config.garmentTypes.length > 0) {
      steps.push(2);
    }
    setCompletedSteps(steps);
  },[config]);

  const handleImageUpload=(field,event)=> {
    const file=event.target.files[0];
    if (file) {
      const reader=new FileReader();
      reader.onload=(e)=> {
        updateConfig({[field]: e.target.result});
      };
      reader.readAsDataURL(file);
    }
  };

  const addGarmentType=()=> {
    if (newGarment.trim() && !config.garmentTypes.some(g=> g.name===newGarment.trim())) {
      const garmentConfig={
        name: newGarment.trim(),
        sizeType: newGarmentSizeType,
        sizeRange: {...newGarmentSizeRange}
      };
      // Actualizar la estructura de garmentTypes para que sea un array de objetos
      const updatedGarments=config.garmentTypes.map(g=>
        typeof g==='string'
          ? {name: g,sizeType: g==='CHALECO' ? 'numeric' : 'american',sizeRange: g==='CHALECO' ? {min: '0',max: '7'} : {min: 'XS',max: '6XL'}}
          : g
      );
      updateConfig({garmentTypes: [...updatedGarments,garmentConfig]});
      setNewGarment('');
      setNewGarmentSizeType('american');
      setNewGarmentSizeRange({min: 'XS',max: '6XL'});
    }
  };

  const removeGarmentType=(index)=> {
    const newTypes=config.garmentTypes.filter((_,i)=> i !==index);
    updateConfig({garmentTypes: newTypes});
  };

  const updateGarmentConfig=(index,field,value)=> {
    const updatedGarments=[...config.garmentTypes];
    updatedGarments[index]={...updatedGarments[index],[field]: value};
    updateConfig({garmentTypes: updatedGarments});
  };

  const addMeasurer=()=> {
    if (newMeasurerName.trim()) {
      const newMeasurer={
        id: Date.now(),
        name: newMeasurerName.trim(),
        signature: ''
      };
      updateConfig({measurers: [...(config.measurers || []),newMeasurer]});
      setNewMeasurerName('');
    }
  };

  const updateMeasurerSignature=(measurerId,signature)=> {
    const updatedMeasurers=(config.measurers || []).map(measurer=>
      measurer.id===measurerId ? {...measurer,signature} : measurer
    );
    updateConfig({measurers: updatedMeasurers});
  };

  const removeMeasurer=(measurerId)=> {
    const updatedMeasurers=(config.measurers || []).filter(
      measurer=> measurer.id !==measurerId
    );
    updateConfig({measurers: updatedMeasurers});
  };

  const handleClearAll=()=> {
    if (window.confirm('⚠️ ¡ATENCIÓN! Esta acción eliminará permanentemente todos los datos de la aplicación, incluyendo:\n\n• Configuración del cliente\n• Datos de personal de tallaje\n• Tipos de prendas\n• Todos los documentos guardados\n• Firmas y fotos\n• Datos importados de usuarios\n\n¿Estás completamente seguro de que deseas borrar todos los datos?')) {
      clearAllData();
      alert('✅ Todos los datos han sido borrados correctamente.');
    }
  };

  const handleShare=async ()=> {
    const shareUrl='https://gilded-dango-6ec6ba.netlify.app';
    const shareData={
      title: 'ITURRI | Tallaje',
      text: 'Aplicación de tallaje de vestuario',
      url: shareUrl
    };
    try {
      // Verificar si el navegador soporta la API Web Share
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        // Fallback para navegadores que no soportan Web Share API
        if (navigator.clipboard) {
          await navigator.clipboard.writeText(shareUrl);
          alert('Enlace copiado al portapapeles: ' + shareUrl);
        } else {
          // Fallback aún más básico
          const textArea=document.createElement('textarea');
          textArea.value=shareUrl;
          textArea.style.position='fixed';
          textArea.style.opacity='0';
          document.body.appendChild(textArea);
          textArea.focus();
          textArea.select();
          document.execCommand('copy');
          document.body.removeChild(textArea);
          alert('Enlace copiado al portapapeles: ' + shareUrl);
        }
      }
    } catch (error) {
      console.error('Error al compartir:',error);
      if (error.name !=='AbortError') {
        alert('No se pudo compartir el enlace. Por favor,inténtalo de nuevo.');
      }
    }
  };

  const handleImportSuccess=(importedUsers)=> {
    if (onUsersUpdate) {
      onUsersUpdate();
    }
  };

  const handleSaveConfiguration=()=> {
    if (!config.clientName || config.clientName.trim()==='') {
      alert('Por favor,configura el nombre del cliente antes de guardar.');
      return;
    }
    
    if (!config.garmentTypes || config.garmentTypes.length===0) {
      alert('Por favor,configura al menos un tipo de prenda antes de guardar.');
      return;
    }
    
    setIsSaving(true);
    
    // Simular un pequeño retraso para mostrar el estado de guardado
    setTimeout(()=> {
      if (onConfigSaved) {
        onConfigSaved();
      }
      setIsSaving(false);
      alert('¡Configuración guardada correctamente! Ahora puedes acceder a las demás secciones de la aplicación.');
    },500);
  };

  const getSizeOptions=(sizeType,sizeRange)=> {
    if (sizeType==='american') {
      const sizes=['XS','S','M','L','XL','2XL','3XL','4XL','5XL','6XL'];
      const startIndex=sizes.indexOf(sizeRange.min);
      const endIndex=sizes.indexOf(sizeRange.max);
      return startIndex !==-1 && endIndex !==-1 ? sizes.slice(startIndex,endIndex + 1) : sizes;
    } else if (sizeType==='european') {
      const sizes=[];
      const min=parseInt(sizeRange.min) || 34;
      const max=parseInt(sizeRange.max) || 58;
      for (let i=min;i <=max;i +=2) {
        sizes.push(i.toString());
      }
      return sizes;
    } else if (sizeType==='numeric') {
      const min=parseInt(sizeRange.min) || 0;
      const max=parseInt(sizeRange.max) || 7;
      const sizes=[];
      for (let i=min;i <=max;i++) {
        sizes.push(i.toString());
      }
      return sizes;
    }
    return [];
  };

  const americanSizes=['XS','S','M','L','XL','2XL','3XL','4XL','5XL','6XL'];
  const europeanSizes=['34','36','38','40','42','44','46','48','50','52','54','56','58'];
  const numericSizes=['0','1','2','3','4','5','6','7','8','9','10'];

  const getSizeRangeForType=(type)=> {
    switch (type) {
      case 'american':
        return {min: 'XS',max: '6XL'};
      case 'european':
        return {min: '34',max: '58'};
      case 'numeric':
        return {min: '0',max: '7'};
      default:
        return {min: 'XS',max: '6XL'};
    }
  };

  const getSizeOptionsForType=(type)=> {
    switch (type) {
      case 'american':
        return americanSizes;
      case 'european':
        return europeanSizes;
      case 'numeric':
        return numericSizes;
      default:
        return americanSizes;
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
      <h1 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6">Configuración</h1>
      
      {/* Indicador de onboarding */}
      {isFirstAccess && (
        <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <div className="flex items-center mb-3">
            <h2 className="text-lg font-semibold text-blue-800">Bienvenido a la configuración inicial</h2>
          </div>
          <div className="space-y-3">
            {onboardingSteps.map((step,index)=> (
              <div key={step.id} className={`flex items-center p-3 rounded-lg ${activeStep===index ? 'bg-blue-100 border border-blue-300' : completedSteps.includes(step.id) ? 'bg-green-50 border border-green-200' : 'bg-white border border-gray-200'}`} >
                <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center mr-3 ${completedSteps.includes(step.id) ? 'bg-green-500 text-white' : activeStep===index ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-600'}`}>
                  {completedSteps.includes(step.id) ? (
                    <SafeIcon icon={FiIcons.FiCheck} className="w-4 h-4" />
                  ) : (
                    <span className="text-sm font-medium">{index + 1}</span>
                  )}
                </div>
                <div>
                  <h3 className="font-medium text-sm">{step.title}</h3>
                  <p className="text-xs text-gray-600">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 text-sm text-blue-700">
            <p>Sigue estos pasos para configurar la aplicación y comenzar a usarla.</p>
          </div>
        </div>
      )}

      <div className="space-y-4 sm:space-y-6">
        <div>
          <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">
            Datos del Cliente <span className="text-red-500">*</span>
          </h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                Nombre del Cliente <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={config.clientName}
                onChange={(e)=> updateConfig({clientName: e.target.value})}
                className="w-full p-2 sm:p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                placeholder="Nombre de la empresa cliente (obligatorio)"
                required
              />
              {!config.clientName && (
                <p className="text-xs text-red-500 mt-1">
                  Este campo es obligatorio para generar documentos
                </p>
              )}
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                Logo del Cliente (opcional)
              </label>
              <div className="space-y-2">
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e)=> handleImageUpload('clientLogo',e)}
                  className="w-full p-2 sm:p-3 border border-gray-300 rounded-lg text-sm"
                />
                {config.clientLogo && (
                  <div className="mt-2">
                    <img src={config.clientLogo} alt="Client Logo Preview" className="h-12 sm:h-16 object-contain border rounded" />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        
        <FileImport onImportSuccess={handleImportSuccess} />
        
        <div>
          <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">
            Personal de Tallaje
          </h3>
          <div className="space-y-4">
            {/* Añadir nuevo tallador */}
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                type="text"
                value={newMeasurerName}
                onChange={(e)=> setNewMeasurerName(e.target.value)}
                onKeyPress={(e)=> e.key==='Enter' && addMeasurer()}
                className="flex-1 p-2 sm:p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                placeholder="Nombre del nuevo tallador"
              />
              <button
                onClick={addMeasurer}
                className="flex items-center justify-center px-3 sm:px-4 py-2 sm:py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
              >
                Añadir Tallador
              </button>
            </div>
            
            {/* Lista de talladores */}
            <div className="space-y-4">
              {(config.measurers || []).map((measurer,index)=> (
                <div key={measurer.id} className="p-4 border border-gray-200 rounded-lg bg-gray-50 relative">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                      <h4 className="font-medium text-sm sm:text-base mb-1">Tallador {index + 1}</h4>
                      <input
                        type="text"
                        value={measurer.name}
                        onChange={(e)=> {
                          const updatedMeasurers=(config.measurers || []).map(m=>
                            m.id===measurer.id ? {...m,name: e.target.value} : m
                          );
                          updateConfig({measurers: updatedMeasurers});
                        }}
                        className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm"
                        placeholder="Nombre del tallador"
                      />
                    </div>
                    <button
                      onClick={()=> removeMeasurer(measurer.id)}
                      className="ml-2 p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
                      title="Eliminar tallador"
                    >
                      <SafeIcon icon={FiIcons.FiTrash2} className="w-5 h-5" />
                    </button>
                  </div>
                  <div className="space-y-2">
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                      Firma del Tallador
                    </label>
                    <SignatureCanvas
                      value={measurer.signature}
                      onChange={(value)=> updateMeasurerSignature(measurer.id,value)}
                      placeholder={`Firma de ${measurer.name || 'tallador'}`}
                      showPreview={false}
                    />
                  </div>
                </div>
              ))}
              {(config.measurers || []).length===0 && (
                <div className="text-center py-8 text-gray-500 text-sm">
                  No hay talladores configurados. Añada un tallador para comenzar.
                </div>
              )}
            </div>
          </div>
        </div>
        
        <div>
          <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">Tipos de Prendas</h3>
          <div className="space-y-4">
            {/* Añadir nueva prenda - Tarjeta diferenciada */}
            <div className="p-4 border-2 border-dashed border-blue-300 rounded-lg bg-blue-50 hover:bg-blue-100 transition-colors">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-bold text-blue-700 text-sm">Añadir Nueva Prenda</h4>
                <div className="bg-blue-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                  +
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex flex-col sm:flex-row gap-2">
                  <input
                    type="text"
                    value={newGarment}
                    onChange={(e)=> setNewGarment(e.target.value)}
                    className="flex-1 p-2 border border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm bg-white"
                    placeholder="Nombre de la prenda (ej: CAMISA,CHAQUETA,etc.)"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-blue-700 mb-1">Sistema de Tallas</label>
                    <select
                      value={newGarmentSizeType}
                      onChange={(e)=> {
                        const type=e.target.value;
                        setNewGarmentSizeType(type);
                        setNewGarmentSizeRange(getSizeRangeForType(type));
                      }}
                      className="w-full p-2 border border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm bg-white"
                    >
                      <option value="american">Americano (XS,S,M,L,XL...)</option>
                      <option value="european">Europeo (34,36,38...58)</option>
                      <option value="numeric">Numérico (0,1,2,3...)</option>
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs font-medium text-blue-700 mb-1">Talla Mínima</label>
                      <select
                        value={newGarmentSizeRange.min}
                        onChange={(e)=>
                          setNewGarmentSizeRange({...newGarmentSizeRange,min: e.target.value})
                        }
                        className="w-full p-2 border border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm bg-white"
                      >
                        {getSizeOptionsForType(newGarmentSizeType).map(size=> (
                          <option key={size} value={size}>{size}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-blue-700 mb-1">Talla Máxima</label>
                      <select
                        value={newGarmentSizeRange.max}
                        onChange={(e)=>
                          setNewGarmentSizeRange({...newGarmentSizeRange,max: e.target.value})
                        }
                        className="w-full p-2 border border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm bg-white"
                      >
                        {getSizeOptionsForType(newGarmentSizeType).map(size=> (
                          <option key={size} value={size}>{size}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div className="text-xs text-blue-700">
                    <strong>Vista previa de tallas:</strong>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {getSizeOptions(newGarmentSizeType,newGarmentSizeRange).map(size=> (
                        <span key={size} className="px-2 py-1 bg-blue-200 text-blue-800 rounded text-xs">
                          {size}
                        </span>
                      ))}
                    </div>
                  </div>
                  <button
                    onClick={addGarmentType}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                  >
                    Añadir Prenda
                  </button>
                </div>
              </div>
            </div>
            
            {/* Lista de prendas existentes */}
            <div className="space-y-3">
              {config.garmentTypes.map((garment,index)=> {
                const garmentName=typeof garment==='string' ? garment : garment.name;
                const sizeType=typeof garment==='string' ? (garment==='CHALECO' ? 'numeric' : 'american') : garment.sizeType;
                const sizeRange=typeof garment==='string' ? (garment==='CHALECO' ? {min: '0',max: '7'} : {min: 'XS',max: '6XL'}) : garment.sizeRange;
                return (
                  <div key={index} className="p-4 border border-gray-200 rounded-lg bg-gray-50">
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex-1">
                        <h4 className="font-medium text-sm mb-2">{garmentName}</h4>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Sistema de Tallas</label>
                            <select
                              value={sizeType}
                              onChange={(e)=> {
                                const type=e.target.value;
                                const newRange=getSizeRangeForType(type);
                                updateGarmentConfig(index,'sizeType',type);
                                updateGarmentConfig(index,'sizeRange',newRange);
                              }}
                              className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm"
                            >
                              <option value="american">Americano</option>
                              <option value="european">Europeo</option>
                              <option value="numeric">Numérico</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Talla Mínima</label>
                            <select
                              value={sizeRange.min}
                              onChange={(e)=>
                                updateGarmentConfig(index,'sizeRange',{...sizeRange,min: e.target.value})
                              }
                              className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm"
                            >
                              {getSizeOptionsForType(sizeType).map(size=> (
                                <option key={size} value={size}>{size}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Talla Máxima</label>
                            <select
                              value={sizeRange.max}
                              onChange={(e)=>
                                updateGarmentConfig(index,'sizeRange',{...sizeRange,max: e.target.value})
                              }
                              className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm"
                            >
                              {getSizeOptionsForType(sizeType).map(size=> (
                                <option key={size} value={size}>{size}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={()=> removeGarmentType(index)}
                        className="ml-2 p-2 text-red-600 hover:text-red-800 transition-colors"
                        title="Eliminar prenda"
                      >
                        ✕
                      </button>
                    </div>
                    <div className="text-xs text-gray-600">
                      <strong>Tallas disponibles:</strong>
                      <div className="mt-1 flex flex-wrap gap-1">
                        {getSizeOptions(sizeType,sizeRange).map(size=> (
                          <span key={size} className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">
                            {size}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}
              {config.garmentTypes.length===0 && (
                <div className="text-center py-8 text-gray-500 text-sm">
                  No hay prendas configuradas. Añada prendas para comenzar.
                </div>
              )}
            </div>
          </div>
        </div>
        
        <div className="pt-4 sm:pt-6 border-t">
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
            <button
              onClick={handleSaveConfiguration}
              disabled={isSaving}
              className={`flex items-center justify-center px-4 sm:px-6 py-2 sm:py-3 rounded-lg transition-colors text-sm ${
                isSaving 
                  ? 'bg-gray-400 text-white cursor-not-allowed' 
                  : 'bg-blue-500 text-white hover:bg-blue-600'
              }`}
            >
              {isSaving ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Guardando...
                </>
              ) : (
                <>
                  <SafeIcon icon={FiIcons.FiSave} className="w-4 h-4 mr-2" />
                  Guardar Configuración
                </>
              )}
            </button>
            
            <button
              onClick={handleShare}
              className="flex items-center justify-center px-4 sm:px-6 py-2 sm:py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
            >
              <SafeIcon icon={FiIcons.FiShare2} className="w-4 h-4 mr-2" />
              Compartir Aplicación
            </button>
            
            <button
              onClick={handleClearAll}
              className="flex items-center justify-center px-4 sm:px-6 py-2 sm:py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm"
            >
              <SafeIcon icon={FiIcons.FiTrash2} className="w-4 h-4 mr-2" />
              Borrar Todos los Datos
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ConfigPage;